/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { cn } from '@/lib/utils';
import {
    tableBodyVariants,
    tableCellVariants,
    tableContainerVariants,
    tableHeadVariants,
    tableHeaderCellVariants,
    tableRowVariants,
    tableVariants,
    type TableCellAlign,
    type TableOverflow,
} from '@/components/ui/table-styles';

/**
 * Table — shadcn's Table, a styled **native** `<table>` rather than a Radix primitive
 * (CRCB2B-636). Styled from Figma `Table` (`455:2481`); all class strings live in
 * `table-styles.ts` so a future non-table structure can wear the same appearance without
 * re-deriving it.
 *
 * ## Deliberately unopinionated about data
 *
 * No sorting, filtering, or pagination. Each consuming implementation layers what it
 * actually needs — order history, a spec sheet, and a saved-cart list want three different
 * sorting models, and baking one in would make two of them fight it. Figma's header cell
 * does carry an optional `swap_vert` affordance; a consumer that sorts renders its own
 * control as header-cell children.
 *
 * ## The header row is optional
 *
 * Both designed shapes come from this one primitive: a multi-column data table WITH a grey
 * header row, per-column alignment and a trailing actions column; and a two-column
 * attribute/value table with NO header row, muted labels and right-aligned values — the PDP
 * Specifications shape. Nothing needs a placeholder header to get the second one.
 *
 * ## `role="table"`, never `role="grid"`
 *
 * Emitting native `table`/`thead`/`tbody`/`tr`/`th`/`td` means the roles come for free. A
 * `grid` role would promise arrow-key cell navigation that is not provided; interactive cell
 * content alone does not warrant it.
 */
function Table({
    className,
    containerClassName,
    containerProps,
    overflow = 'scroll',
    ...props
}: React.ComponentProps<'table'> & {
    /** Class names for the scroll container that wraps the table. */
    containerClassName?: string;
    /**
     * Props for the scroll container. Use it to name the scroll region —
     * `containerProps={{ 'aria-label': 'Recent orders' }}` — so the focus stop announces what it
     * is rather than as a bare unnamed region.
     */
    containerProps?: React.ComponentProps<'div'>;
    overflow?: TableOverflow;
}) {
    const scrollable = overflow === 'scroll';

    return (
        <div
            data-slot="table-container"
            // A horizontally scrolling region MUST be keyboard operable, or a keyboard-only user
            // cannot reach the columns past the fold. axe's `scrollable-region-focusable` catches
            // it, and it fired here for real: a spec table has no interactive cells, so nothing
            // inside could receive focus and carry the scroll.
            //
            // The tab stop is tied to `overflow="scroll"` rather than measured, because that
            // variant is the consumer explicitly declaring "this scrolls". A table that never
            // overflows should pass `overflow="contain"` and then costs no tab stop at all. If a
            // page ever accumulates enough always-scroll tables for the stops to feel noisy, the
            // precise fix is to measure `scrollWidth > clientWidth` and make it conditional.
            tabIndex={scrollable ? 0 : undefined}
            // `group`, deliberately NOT `region`. `region` is a landmark, and a table is very
            // often nested inside one already — an accordion panel is `role="region"` labelled by
            // its trigger, so a table region sharing that name trips axe's `landmark-unique`
            // ("landmarks must have a unique role/label combination"). That fired for real in the
            // accordion's structured-content story. `group` names the focus stop without minting
            // a landmark, and `scrollable-region-focusable` only needs the element to be
            // focusable, which `tabIndex` already handles.
            //
            // Applied only when named: an unnamed group tells the user nothing.
            role={
                scrollable && (containerProps?.['aria-label'] || containerProps?.['aria-labelledby'])
                    ? 'group'
                    : undefined
            }
            className={cn(tableContainerVariants({ overflow }), containerClassName)}
            {...containerProps}>
            <table data-slot="table" className={cn(tableVariants(), className)} {...props} />
        </div>
    );
}

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
    return <thead data-slot="table-header" className={cn(tableHeadVariants(), className)} {...props} />;
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
    return <tbody data-slot="table-body" className={cn(tableBodyVariants(), className)} {...props} />;
}

function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
    return <tfoot data-slot="table-footer" className={cn('bg-muted font-book', className)} {...props} />;
}

function TableRow({
    className,
    interactive,
    ...props
}: React.ComponentProps<'tr'> & {
    /**
     * Whether the row shows the hover treatment. Header rows pass `false` — a grey header
     * lighting up on hover reads as interactive when it is not.
     */
    interactive?: boolean;
}) {
    return (
        <tr data-slot="table-row" className={cn(tableRowVariants({ interactive }), className)} {...props} />
    );
}

/**
 * Header cell. `scope="col"` is the default because a column header is the overwhelmingly
 * common case; a row header passes `scope="row"`. Do not drop `scope` — it is one of the
 * things a div binding cannot express at all.
 */
function TableHead({
    className,
    align,
    scope = 'col',
    ...props
}: Omit<React.ComponentProps<'th'>, 'align'> & { align?: TableCellAlign }) {
    return (
        <th
            data-slot="table-head"
            scope={scope}
            className={cn(tableHeaderCellVariants({ align }), className)}
            {...props}
        />
    );
}

function TableCell({
    className,
    align,
    wrap,
    ...props
}: Omit<React.ComponentProps<'td'>, 'align'> & {
    align?: TableCellAlign;
    /**
     * Opt this cell into wrapping. Off by default, matching Figma's `Type=Default`: the
     * value stays on one line and ellipsises. Turn it on for text columns where the full
     * value matters; leave it off for amounts, identifiers and dates.
     */
    wrap?: boolean;
}) {
    return (
        <td
            data-slot="table-cell"
            className={cn(tableCellVariants({ align, wrap }), className)}
            {...props}
        />
    );
}

function TableCaption({ className, ...props }: React.ComponentProps<'caption'>) {
    return (
        <caption
            data-slot="table-caption"
            className={cn('mt-4 text-sm font-book tracking-[0.03em] text-muted-foreground', className)}
            {...props}
        />
    );
}

export {
    Table,
    TableHeader,
    TableBody,
    TableFooter,
    TableHead,
    TableRow,
    TableCell,
    TableCaption,
};
